'use client';

import { useRouter } from 'next/navigation';
import { Modal } from '@/app/components/Modal';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { CLIENTS_CREATE_PATH } from '@/consts/paths';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { useCreateClientMutation } from '../../create/hooks/useCreateClientMutation';
import { SelectDraftModal } from '../SelectDraftModal';
import FileIcon from '/public/icons/file-2.svg';
import PencilIcon from '/public/icons/pencil-2.svg';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToCreate: () => void;
  isSelectDraftModalOpen: boolean;
  setIsSelectDraftModalOpen: (isOpen: boolean) => void;
}

export const CreateClientModal = ({
  isOpen,
  onClose,
  onNavigateToCreate,
  isSelectDraftModalOpen,
  setIsSelectDraftModalOpen,
}: CreateClientModalProps) => {
  const router = useRouter();
  const [, updateQuery] = useSearchQuery();
  const { mutateAsync: createClient, isPending } = useCreateClientMutation({
    onSuccess: (response) => {
      updateQuery({ [CLIENT_ID_QUERY_KEY]: String(response.data.id) });
    },
  });

  const handleStartFromScratch = async () => {
    try {
      const response = await createClient();
      if (response.data.id) {
        onNavigateToCreate();
        router.push(`${CLIENTS_CREATE_PATH}?client_id=${response.data.id}`);
      }
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const handleChooseFromDrafts = () => {
    setIsSelectDraftModalOpen(true);
  };

  const handleGoBack = () => {
    setIsSelectDraftModalOpen(false);
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <>
        {isSelectDraftModalOpen ? (
          <SelectDraftModal onGoBack={handleGoBack} />
        ) : (
          <div className="py-8 px-4 flex flex-col items-center gap-7.5 w-[calc(100vw-2rem)] max-w-[592px] md:w-[592px] md:h-[335px] md:py-11.5 md:px-9">
            <h2 className="light text-blue-900 text-center max-md:px-8">
              How do you want to start creating <br className="hidden md:block" />a client?
            </h2>

            <div className="flex flex-col gap-5 w-full md:flex-row">
              <button
                onClick={handleStartFromScratch}
                disabled={isPending}
                className="flex flex-col items-center gap-4 w-full md:w-1/2 px-4 py-5 hover:shadow-card border border-grey-400 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-grey-400"
              >
                <PencilIcon className="w-8 h-8" />
                <div className="flex flex-col items-center gap-0.5">
                  <h5 className="text-black">Start from scratch</h5>
                  <p className="text-body-regular text-grey-700 whitespace-nowrap overflow-hidden">
                    Create a new client profile
                  </p>
                </div>
              </button>

              <button
                onClick={handleChooseFromDrafts}
                disabled={isPending}
                className="flex flex-col items-center gap-4 w-full md:w-1/2 px-4 py-5 hover:shadow-card border border-grey-400 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-grey-400"
              >
                <FileIcon className="w-8 h-8 text-blue-300 stroke-blue-300" />
                <div className="flex flex-col items-center gap-0.5">
                  <h5 className="text-black">Choose from drafts</h5>
                  <p className="text-body-regular text-grey-700 whitespace-nowrap overflow-hidden">
                    Continue from a saved draft.
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}
      </>
    </Modal>
  );
};
